import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Bell, Clock, X, Check } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { toast } from 'sonner';
import { useReminders, useUpdateReminder } from '../lib/queries';

function detectTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

// Wheel column — iOS-style scrollable list where the selected value is at center.
function WheelColumn({ values, value, onChange, isDark, testId }) {
  const ITEM_H = 44; // px
  const ref = useRef(null);
  const timerRef = useRef(null);

  // Snap to initial value on mount / value change (without smooth behavior)
  useEffect(() => {
    const idx = Math.max(0, values.indexOf(value));
    if (ref.current) ref.current.scrollTop = idx * ITEM_H;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const idx = Math.max(0, values.indexOf(value));
    if (ref.current && Math.abs(ref.current.scrollTop - idx * ITEM_H) > 2) {
      // only snap if really different (avoid jitter while user scrolls)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleScroll = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (!ref.current) return;
      const idx = Math.round(ref.current.scrollTop / ITEM_H);
      const clamped = Math.max(0, Math.min(values.length - 1, idx));
      const snappedTop = clamped * ITEM_H;
      if (Math.abs(ref.current.scrollTop - snappedTop) > 1) {
        ref.current.scrollTo({ top: snappedTop, behavior: 'smooth' });
      }
      const v = values[clamped];
      if (v !== value) onChange(v);
    }, 120);
  };

  const selectedIdx = Math.max(0, values.indexOf(value));

  return (
    <div
      ref={ref}
      onScroll={handleScroll}
      className="relative h-[220px] overflow-y-auto no-scrollbar"
      style={{ scrollSnapType: 'y mandatory', WebkitOverflowScrolling: 'touch' }}
      data-testid={testId}
    >
      {/* top spacer (≈2 items so the first becomes centerable) */}
      <div style={{ height: ITEM_H * 2 }} aria-hidden />
      {values.map((v, i) => {
        const dist = Math.abs(i - selectedIdx);
        const opacity = dist === 0 ? 1 : dist === 1 ? 0.55 : dist === 2 ? 0.3 : 0.15;
        const scale = dist === 0 ? 1 : 0.88;
        return (
          <div
            key={v}
            onClick={() => {
              onChange(v);
              if (ref.current) ref.current.scrollTo({ top: i * ITEM_H, behavior: 'smooth' });
            }}
            className={`flex items-center justify-center cursor-pointer font-mono tabular-nums transition-opacity ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}
            style={{
              height: ITEM_H,
              scrollSnapAlign: 'center',
              fontSize: dist === 0 ? '2rem' : '1.5rem',
              opacity,
              transform: `scale(${scale})`,
              transition: 'opacity 120ms, transform 120ms, font-size 120ms',
              fontWeight: dist === 0 ? 700 : 500,
            }}
          >
            {v}
          </div>
        );
      })}
      <div style={{ height: ITEM_H * 2 }} aria-hidden />
    </div>
  );
}

// Centered modal with two scrollable wheel columns (iOS style) + pink Confirm.
function TimePickerModal({ initialTime, isDark, onCancel, onConfirm }) {
  const [hh, setHh] = useState(() => (initialTime || '08:00').split(':')[0] || '08');
  const [mm, setMm] = useState(() => (initialTime || '08:00').split(':')[1] || '00');

  const pad = (n) => String(n).padStart(2, '0');
  const hours = Array.from({ length: 24 }, (_, i) => pad(i));
  const minutes = Array.from({ length: 60 }, (_, i) => pad(i));

  return createPortal(
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-[9999] p-4"
      onClick={onCancel}
      data-testid="time-picker-overlay"
    >
      <div
        className={`w-full max-w-sm rounded-3xl overflow-hidden shadow-2xl ${
          isDark ? 'bg-[#1a1a1a] border border-white/10' : 'bg-white border border-gray-200'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`px-5 py-4 flex items-center justify-between border-b ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
          <div className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Imposta orario</div>
          <button
            onClick={onCancel}
            className={`${isDark ? 'text-white/60 hover:text-white' : 'text-gray-400 hover:text-gray-600'}`}
            data-testid="time-picker-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Wheel picker body */}
        <div className="px-5 py-4 relative">
          {/* center highlight band */}
          <div
            className={`pointer-events-none absolute left-5 right-5 rounded-xl ${
              isDark ? 'bg-white/5 border border-white/10' : 'bg-gray-50 border border-gray-200'
            }`}
            style={{ top: 'calc(50% - 22px)', height: 44 }}
            aria-hidden
          />
          <div className="flex items-center justify-center gap-3 relative">
            <WheelColumn
              values={hours}
              value={hh}
              onChange={setHh}
              isDark={isDark}
              testId="time-picker-hours"
            />
            <div className={`text-3xl font-mono font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>:</div>
            <WheelColumn
              values={minutes}
              value={mm}
              onChange={setMm}
              isDark={isDark}
              testId="time-picker-minutes"
            />
          </div>
        </div>

        <div className={`p-4 flex items-center gap-2 border-t ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
          <button
            onClick={onCancel}
            className={`flex-1 py-3 rounded-xl font-medium ${isDark ? 'bg-white/5 text-white/80' : 'bg-gray-100 text-gray-700'}`}
            data-testid="time-picker-cancel"
          >
            Annulla
          </button>
          <button
            onClick={() => onConfirm(`${hh}:${mm}`)}
            className="flex-1 py-3 rounded-xl font-semibold bg-[#E8334A] hover:bg-[#d42a40] text-white flex items-center justify-center gap-2 shadow-lg shadow-[#E8334A]/20"
            data-testid="time-picker-confirm"
          >
            <Check className="w-4 h-4" /> Conferma
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function RemindersSection() {
  const { t, language } = useLanguage();
  const { isDark } = useTheme();
  const { data: reminders = [], isLoading } = useReminders();
  const updateMut = useUpdateReminder();
  const [pickerReminderId, setPickerReminderId] = useState(null);

  const updateReminder = (reminder, patch) => {
    const newEnabled = patch.enabled !== undefined ? patch.enabled : reminder.user_enabled;
    const newTime = patch.time !== undefined ? patch.time : (reminder.user_time || reminder.default_time || '08:00');
    const tz = reminder.user_timezone && reminder.user_timezone !== 'UTC'
      ? reminder.user_timezone
      : detectTimezone();

    updateMut.mutate(
      { reminderId: reminder.reminder_id, enabled: newEnabled, time: newTime, timezone: tz },
      {
        onSuccess: () => toast.success(t('profile.remindersSaved') || 'Saved'),
        onError: () => toast.error(t('common.error') || 'Error'),
      }
    );
  };

  if (isLoading) return null;
  if (!reminders.length) return null;

  const pickerReminder = reminders.find((r) => r.reminder_id === pickerReminderId);

  // Simplified short titles derived from reminder type (user request: no verbose
  // sentences, just "Promemoria Peso" / "Promemoria Esercizio" in each language).
  const getSimpleTitle = (r) => {
    const typeMap = {
      weight: {
        it: 'Promemoria peso', en: 'Weight reminder', es: 'Recordatorio de peso',
        pt: 'Lembrete de peso', de: 'Gewichts-Erinnerung', fr: 'Rappel de poids',
      },
      workout: {
        it: 'Promemoria esercizio', en: 'Exercise reminder', es: 'Recordatorio de ejercicio',
        pt: 'Lembrete de exercício', de: 'Sport-Erinnerung', fr: 'Rappel d\'exercice',
      },
    };
    const entry = typeMap[r.type];
    if (entry) return entry[language] || entry.it;
    // Fallback: strip emojis from backend title
    const backendTitle = (r.title_translations || {})[language] || (r.title_translations || {}).it || '';
    return backendTitle.replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/gu, '').trim();
  };

  return (
    <div className="glass-card-sm p-4" data-testid="reminders-section">
      {/* Header: bell icon + title + subtitle (left-aligned, vertically centered) */}
      <div className="flex items-center gap-3 pb-4">
        <Bell className={`w-5 h-5 flex-shrink-0 ${isDark ? 'text-white/60' : 'text-gray-500'}`} />
        <div className="min-w-0">
          <div className={`font-semibold text-base ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {t('profile.reminders') || 'Reminders'}
          </div>
          <div className={`text-xs mt-0.5 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
            {t('profile.remindersSubtitle') || ''}
          </div>
        </div>
      </div>

      {/* Reminder rows — separated by thin dividers, not individual cards */}
      <div className="space-y-0">
        {reminders.map((r, idx) => {
          const isSaving = updateMut.isPending && updateMut.variables?.reminderId === r.reminder_id;
          const currentTime = r.user_time || r.default_time || '08:00';
          return (
            <div
              key={r.reminder_id}
              className={`py-4 ${idx > 0 ? (isDark ? 'border-t border-white/10' : 'border-t border-gray-200') : ''}`}
              data-testid={`reminder-item-${r.reminder_id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className={`font-semibold text-[15px] ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {getSimpleTitle(r)}
                  </div>
                  {r.skip_if_logged_today && (
                    <div className={`text-xs mt-1 italic ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                      {t('profile.remindersSkipIfLogged') || 'Skipped if already logged today'}
                    </div>
                  )}
                </div>

                {/* Toggle */}
                <button
                  onClick={() => updateReminder(r, { enabled: !r.user_enabled })}
                  disabled={isSaving}
                  className={`w-12 h-7 rounded-full p-1 transition-colors flex-shrink-0 ${
                    r.user_enabled ? 'bg-[#E8334A]' : isDark ? 'bg-white/10' : 'bg-gray-200'
                  }`}
                  data-testid={`reminder-toggle-${r.reminder_id}`}
                  aria-label={t('profile.remindersEnable') || 'Enable'}
                >
                  <div className={`w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
                    r.user_enabled ? 'translate-x-5' : 'translate-x-0'
                  }`} />
                </button>
              </div>

              {/* Time picker button — only when reminder is enabled */}
              {r.user_enabled && (
                <button
                  onClick={() => setPickerReminderId(r.reminder_id)}
                  disabled={isSaving}
                  className={`mt-3 w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors ${
                    isDark ? 'bg-white/5 hover:bg-white/10 text-white' : 'bg-gray-50 hover:bg-gray-100 text-gray-900'
                  }`}
                  data-testid={`reminder-time-btn-${r.reminder_id}`}
                >
                  <span className="flex items-center gap-2 text-sm">
                    <Clock className="w-4 h-4" />
                    {t('profile.remindersTime') || 'Time'}
                  </span>
                  <span className="text-lg font-mono tabular-nums font-semibold">{currentTime}</span>
                </button>
              )}
            </div>
          );
        })}
      </div>

      {pickerReminder && (
        <TimePickerModal
          initialTime={pickerReminder.user_time || pickerReminder.default_time || '08:00'}
          isDark={isDark}
          onCancel={() => setPickerReminderId(null)}
          onConfirm={(newTime) => {
            setPickerReminderId(null);
            updateReminder(pickerReminder, { time: newTime });
          }}
        />
      )}
    </div>
  );
}
